import pdfMake from 'pdfmake/build/pdfmake'
import * as pdfFonts from 'pdfmake/build/vfs_fonts'
import type { Content, TDocumentDefinitions } from 'pdfmake/interfaces'
import { logError } from '@/utils/errorUtils'
import { pdfBytesToBase64, urlToDataURL } from '../OrderTests/pdf/pdfHelpers'
import { BRAND_BLUE, TABLE_LINE_COLOR, TEXT_SECONDARY } from '../OrderTests/pdf/constants'
import logo from '/logo.png'

const vfs =
  (pdfFonts as Record<string, unknown>).vfs ||
  (pdfFonts as Record<string, Record<string, unknown>>).pdfMake?.vfs

if (vfs) {
  pdfMake.vfs = vfs as Record<string, string>
}

type FollowupInstructionsPdfInput = {
  instructionsSubject: string
  instructionsBody: string
  patient?: FollowupPdfPatientHeader
  providerName?: string
  sourceName?: string
  providerHeader?: FollowupPdfProviderHeader
}

type FollowupInstructionsPdfOutput = {
  bytes: Uint8Array
  base64: string
  fileName: string
}

export type FollowupPdfProviderHeader = {
  organizationName: string
  providerName: string
  npi: string
  phone: string
  fax: string
  address: string
  city: string
  state: string
  zip: string
  email: string
  accountNumber?: string
}

export type FollowupPdfPatientHeader = {
  name: string
  dateOfBirth: string
  sex: string
  phone: string
  email: string
  address: string
  city: string
  state: string
  zip: string
}

function present(value?: string): string | undefined {
  const trimmed = value?.trim()
  return trimmed && trimmed !== '-' ? trimmed : undefined
}

function buildProviderHeaderStack(provider: FollowupPdfProviderHeader): Content {
  const stack: Content[] = []

  if (provider.organizationName) {
    stack.push({
      text: provider.organizationName,
      fontSize: 9,
      bold: true,
      color: BRAND_BLUE,
      margin: [0, 0, 0, 2],
    })
  }

  if (provider.providerName) {
    const npiPart = provider.npi ? `  |  NPI: ${provider.npi}` : ''
    stack.push({
      text: `${provider.providerName}${npiPart}`,
      fontSize: 7.5,
      color: '#333',
      margin: [0, 0, 0, 1],
    })
  }

  if (provider.accountNumber) {
    stack.push({
      text: `Account #: ${provider.accountNumber}`,
      fontSize: 7,
      bold: true,
      color: '#555',
      margin: [0, 0, 0, 1],
    })
  }

  const contactParts: string[] = []
  if (provider.fax) contactParts.push(`Fax: ${provider.fax}`)
  if (contactParts.length > 0) {
    stack.push({
      text: contactParts.join('  |  '),
      fontSize: 7,
      bold: true,
      color: '#555',
      margin: [0, 0, 0, 1],
    })
  }

  const address = [provider.address, provider.city, provider.state, provider.zip]
    .filter(Boolean)
    .join(', ')
  if (address) {
    stack.push({
      text: address,
      fontSize: 7,
      color: '#555',
      margin: [0, 0, 0, 0],
    })
  }

  return { stack }
}

function buildHeader(logoDataUrl: string, input: FollowupInstructionsPdfInput): Content {
  const providerHeader = input.providerHeader ?? {
    organizationName: present(input.sourceName) ?? '',
    providerName: present(input.providerName) ?? '',
    npi: '',
    phone: '',
    fax: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    email: '',
  }

  return {
    columns: [
      logoDataUrl
        ? { image: logoDataUrl, width: 60, margin: [0, 0, 15, 0] }
        : { text: 'beHuman', fontSize: 11, bold: true, color: '#111827', margin: [0, 0, 16, 0] },
      {
        stack: [buildProviderHeaderStack(providerHeader)],
        margin: [8, 0, 0, 0],
        width: '*',
      },
    ],
    margin: [0, 0, 0, 6],
  }
}

function buildSectionTitle(
  title: string,
  margin: [number, number, number, number] = [0, 6, 0, 2],
): Content {
  return {
    text: title.toUpperCase(),
    fontSize: 8,
    bold: true,
    color: BRAND_BLUE,
    margin,
  }
}

function buildKeyValueTable(rows: [string, string][]): Content {
  const visibleRows = rows.filter(([, value]) => value)

  return {
    table: {
      widths: ['30%', '70%'],
      body: visibleRows.map(([key, value]) => [
        { text: key, fontSize: 7, bold: true, color: TEXT_SECONDARY },
        { text: value, fontSize: 7 },
      ]),
    },
    layout: {
      hLineWidth: () => 0.5,
      vLineWidth: () => 0,
      hLineColor: () => TABLE_LINE_COLOR,
      paddingTop: () => 1,
      paddingBottom: () => 1,
    },
    margin: [0, 0, 0, 2],
  }
}

function buildInstructionsContent(instructionsBody: string): Content[] {
  return instructionsBody
    .split('\n')
    .map((line) => line.trimEnd())
    .map((line) => ({
      text: line || ' ',
      fontSize: 8,
      lineHeight: 1.25,
      margin: [0, 0, 0, line ? 3 : 1],
    }))
}

function getFileName(patientName?: string): string {
  const patientSlug = present(patientName)
    ?.replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase()

  return patientSlug ? `patient-instructions-${patientSlug}.pdf` : 'patient-instructions.pdf'
}

function buildPatientInformationTable(patient?: FollowupPdfPatientHeader): Content {
  const address = patient
    ? [patient.address, patient.city, patient.state, patient.zip].filter(Boolean).join(', ')
    : ''

  return buildKeyValueTable([
    ['Name', present(patient?.name) ?? '-'],
    ['Date of Birth', patient?.dateOfBirth ?? ''],
    ['Sex', patient?.sex ?? ''],
    ['Phone', patient?.phone ?? ''],
    ['Address', address],
  ])
}

export async function generateFollowupInstructionsPdf(
  input: FollowupInstructionsPdfInput,
): Promise<FollowupInstructionsPdfOutput> {
  let logoDataUrl = ''
  try {
    logoDataUrl = await urlToDataURL(logo)
  } catch (err) {
    logError('Follow-up PDF logo fetch', err)
  }

  const generatedAt = new Date()
  const generatedDate = generatedAt.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
  const generatedYear = generatedAt.getFullYear()

  const content: Content[] = [
    buildHeader(logoDataUrl, input),
    {
      text: input.instructionsSubject || 'Patient Instructions',
      fontSize: 13,
      bold: true,
      color: BRAND_BLUE,
      margin: [0, 4, 0, 1],
    },
    {
      text: `Generated on ${generatedDate}`,
      fontSize: 7,
      color: '#999',
      margin: [0, 0, 0, 4],
    },
    {
      canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1, lineColor: BRAND_BLUE }],
      margin: [0, 0, 0, 4],
    },
    buildSectionTitle('Patient Information'),
    buildPatientInformationTable(input.patient),
    buildSectionTitle('Patient Instructions', [0, 8, 0, 6]),
    ...buildInstructionsContent(input.instructionsBody),
  ]

  const copyrightText = `Copyright \u00A9 ${generatedYear} beHuman Corp. The contents of this document are intended only for the use of the individual or entity to which they are addressed and may contain information that is legally privileged, confidential, and exempt from disclosure, including protected health information. If you are not the intended recipient, you are hereby notified that any dissemination, distribution, or copying of this document is strictly prohibited. If you have received or obtained this document in error, please notify the original sender or contact beHuman Corp. by email at privacy@behuman.health and delete this document immediately.`

  const docDefinition: TDocumentDefinitions = {
    pageSize: 'LETTER',
    pageMargins: [36, 30, 36, 65],
    content,
    footer: (currentPage, pageCount) => ({
      stack: [
        {
          columns: [
            {
              text: 'BeHuman Medical Group PA \u2014 Patient Instructions',
              fontSize: 6,
              color: '#999',
              margin: [36, 0, 0, 0],
            },
            {
              text: `Page ${currentPage} of ${pageCount}`,
              fontSize: 6,
              color: '#999',
              alignment: 'right',
              margin: [0, 0, 36, 0],
            },
          ],
        },
        {
          text: copyrightText,
          fontSize: 4.5,
          color: '#aaa',
          margin: [36, 3, 36, 0],
          lineHeight: 1.1,
        },
      ],
    }),
    defaultStyle: {
      fontSize: 8,
    },
  }

  const bytes = await new Promise<Uint8Array>((resolve, reject) => {
    const timeoutId = setTimeout(() => reject(new Error('PDF generation timed out')), 30_000)
    try {
      pdfMake.createPdf(docDefinition).getBuffer((buffer) => {
        clearTimeout(timeoutId)
        resolve(new Uint8Array(buffer))
      })
    } catch (err) {
      clearTimeout(timeoutId)
      reject(err)
    }
  })

  return {
    bytes,
    base64: pdfBytesToBase64(bytes),
    fileName: getFileName(input.patient?.name),
  }
}
